/**
 * Visual vocabulary — maps engine concepts to GBC-friendly glyphs + labels.
 *
 * We deliberately use compact text/Unicode glyphs (no bitmap art) so the look
 * stays tidy + readable with zero asset pipeline. Everything here is presentation
 * only; the engine never sees it.
 */

import type {
  Intent,
  NodeKind,
  StatusKind,
  CardType,
} from "../engine/types";
import { STATUS_DEFS } from "../engine/status";

/** Per-status short label + glyph + class. */
const STATUS_INFO: Record<
  StatusKind,
  { label: string; glyph: string }
> = {
  block: { label: "Block", glyph: "▣" },
  vulnerable: { label: "Vuln", glyph: "▼" },
  weak: { label: "Weak", glyph: "▽" },
  exposed: { label: "Exposed", glyph: "◇" },
  virus: { label: "Virus", glyph: "☣" },
  poison: { label: "Poison", glyph: "✦" },
  strength: { label: "Str", glyph: "▲" },
  dexterity: { label: "Dex", glyph: "◆" },
  regen: { label: "Regen", glyph: "✚" },
  intangible: { label: "Intang", glyph: "○" },
  firewall: { label: "Firewall", glyph: "▦" },
  overclock: { label: "Overclk", glyph: "⚡" },
};

export function statusLabel(k: StatusKind): string {
  return STATUS_INFO[k]?.label ?? k;
}
export function statusGlyph(k: StatusKind): string {
  return STATUS_INFO[k]?.glyph ?? "•";
}
export function statusClass(k: StatusKind): "buff" | "debuff" | "block" | "" {
  if (k === "block") return "block";
  const def = STATUS_DEFS[k];
  if (!def) return "";
  return def.kind_class === "buff"
    ? "buff"
    : def.kind_class === "debuff"
      ? "debuff"
      : "";
}

/** ICE sprite glyph by defId (fallback by elite/boss). */
const ICE_GLYPH: Record<string, string> = {
  sentry: "⊟",
  wisp: "≈",
  leech: "∽",
  spider: "⋔",
  glitcher: "⩫",
  probe: "⊹",
  mauler: "⊠",
  warden_shard: "⛨",
  daemon: "⍓",
  enforcer: "⊞",
  tracer: "⊕",
  shade: "◭",
  swarmling: "·",
  reaper: "⏚",
  juggernaut: "⊡",
  corruptor: "⌗",
  bulwark: "⛉",
  hive: "⌬",
  overseer: "◉",
  warbeast: "⨂",
  warden: "♆",
  sovereign: "♔",
  root: "☢",
};
export function iceGlyph(defId: string): string {
  return ICE_GLYPH[defId] ?? "▮";
}

/** Card archetype/art glyph by type (kept abstract + clean). */
const CARD_TYPE_GLYPH: Record<CardType, string> = {
  attack: "⚔",
  skill: "◈",
  power: "✺",
  status: "▩",
  curse: "☠",
};
export function cardArtGlyph(type: CardType): string {
  return CARD_TYPE_GLYPH[type] ?? "▯";
}

/** Map-node kind -> glyph + display name. */
const NODE_INFO: Record<NodeKind, { glyph: string; name: string }> = {
  combat: { glyph: "⚔", name: "Combat" },
  elite: { glyph: "★", name: "Elite" },
  event: { glyph: "?", name: "Event" },
  shop: { glyph: "$", name: "Shop" },
  rest: { glyph: "♨", name: "Rest" },
  treasure: { glyph: "◈", name: "Cache" },
  boss: { glyph: "☠", name: "Boss" },
};
export function nodeGlyph(k: NodeKind): string {
  return NODE_INFO[k]?.glyph ?? "?";
}
export function nodeName(k: NodeKind): string {
  return NODE_INFO[k]?.name ?? k;
}

/**
 * Render a telegraphed intent into a short, readable label. The engine resolves
 * actual numbers (incl. status mods at resolve time), but the telegraph shows
 * the base values so the player can plan — exactly like StS's intent preview.
 */
export function intentText(intent: Intent): string {
  const label = intent.label ? intent.label + " " : "";
  switch (intent.kind) {
    case "attack": {
      const hits = intent.hits ?? 1;
      const dmg = intent.value ?? 0;
      return hits > 1
        ? `${label}${dmg}×${hits}`
        : `${label}${dmg}`;
    }
    case "attackDebuff": {
      const hits = intent.hits ?? 1;
      const dmg = intent.value ?? 0;
      return hits > 1 ? `${label}${dmg}×${hits}` : `${label}${dmg}`;
    }
    case "defend":
      return `${label}+${intent.value ?? 0}`;
    case "buff":
      return `${label}`.trim() || "Buff";
    case "debuff":
      return `${label}`.trim() || "Debuff";
    case "summon":
      return `${label}`.trim() || "Summon";
    case "trace":
      return `${label}+${intent.traceAmount ?? 0} Trace`;
    case "unknown":
      return "?";
    default:
      return "?";
  }
}

/** A glyph that prefixes the intent label (so attacks read as sword, etc.). */
export function intentGlyph(intent: Intent): string {
  switch (intent.kind) {
    case "attack":
    case "attackDebuff":
      return "⚔";
    case "defend":
      return "▣";
    case "buff":
      return "▲";
    case "debuff":
      return "▽";
    case "summon":
      return "✦";
    case "trace":
      return "◎";
    default:
      return "?";
  }
}

/** CSS class for an intent (color-codes the band). */
export function intentClass(intent: Intent): string {
  return intent.kind;
}
